import { z } from "zod";
import { http, request, responseError } from "@/lib/http";
import {
  readOperatingCurrencyAssignmentV2,
  readPricingContextResponseV2,
  readPricingProfileListResponseV2,
  readPricingProfileResponseV2,
  readTariffResponseV2,
  type ManagementRead,
  type OperatingCurrencyAssignmentV2,
  type PricingContextV2,
  type PricingProfileListManagementV2,
  type PricingProfileManagementV2,
  type TariffManagementV2,
} from "@voltie/localization-web";
import type { HealthStatus } from "@/lib/health";
import { readUnitBillingTotals, type UnitBillingTotalsRead } from "@/lib/unitBillingTotals";
import { readUnitSessionsPage, type UnitSessionsRead } from "@/lib/unitSessions";
import { readOrgSessionsReport, type OrgSessionsReportRead } from "@/lib/orgSessionsReport";
import {
  readSwitchBasis,
  readSwitchCommitted,
  readSwitchRepaired,
  type OperatingCurrencySwitchBasis,
  type OperatingCurrencySwitchCommitted,
  type OperatingCurrencySwitchRepaired,
  type OperatingCurrencySwitchRequest,
  type SwitchRead,
} from "@/lib/operatingCurrencySwitch";
import {QueryObserverResult} from "@tanstack/react-query";

export type QueryResponse<TEntity> = {
  error: Error | null;
  loading: boolean;
  refetch: () => Promise<QueryObserverResult<TEntity, Error>>
}

/* ------------------------------------------------------------------ */
/*  Type Interfaces                                                    */
/* ------------------------------------------------------------------ */

/** Global platform role. "readonly_admin" is a full-fleet auditor: it sees
 *  every admin page but the backend rejects all of its writes. */
export type SystemRole = "admin" | "user" | "readonly_admin";

/** Human-readable label for a global role badge. Unknown values pass through. */
export function systemRoleLabel(role: string): string {
  switch (role) {
    case "admin": return "admin";
    case "readonly_admin": return "read-only admin";
    case "user": return "user";
    default: return role;
  }
}

export interface AuthUser {
  id: number;
  username: string;
  role: SystemRole;
  tier?: string;
}

export interface UserRecord {
  id: number;
  username: string;
  role: SystemRole;
  created_at: string;
  last_login: string;
  session_count: number;
  unit_count?: number;
  // `tier` is the EFFECTIVE tier the server enforces right now (a temporary
  // Prime grant shows here as "prime"). `base_tier` is the account's underlying
  // paid tier, and the prime_* fields describe an active free grant, if any.
  tier?: string;
  base_tier?: string;
  prime_until?: string | null;
  prime_grant_active?: boolean;
  prime_source?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  account_id?: string | null;
  disabled?: boolean;
  locked?: boolean;
  lockout_remaining_seconds?: number;
  failed_attempts?: number;
  email?: string | null;
  email_verified?: boolean;
  phone?: string | null;
  phone_verified?: boolean;
  // Tenant-plane roles: the organizations this user belongs to and the role held
  // in each ('manager' | 'driver'). Distinct from the system `role` above.
  organization_memberships?: Array<{
    organization_id: number;
    organization_name: string;
    role: "manager" | "driver";
  }>;
}

export type EmailStatusFilter = "missing" | "unverified" | "verified";

/** Subscription-plan filter on the EFFECTIVE tier, plus 'grant' = comped users
 *  with an active free-Prime grant. */
export type PlanFilter = "free" | "prime" | "commercial" | "grant";

export interface UsersPage {
  users: UserRecord[];
  next_cursor: number | null;
}

export interface ActiveSessionsResponse {
  active_sessions: ActiveSession[];
  count: number;
  total_count: number;
  offset: number;
  limit: number;
  has_more: boolean;
  no_units_assigned?: boolean;
}

export interface ActiveSession {
  unit_id: string;
  unit_name?: string;
  start_time: string;
  duration_seconds: number;
  energy_delivered: number;
  subsessions_count?: number;
  costs?: {
    // Every amount is null when unknown → rendered as "—", never a stand-in 0. For an
    // active session this is the backend's live estimate (estimate_status says how far
    // it goes); for a finished one the persisted billing (see resolve_session_billing).
    total_cost: number | null;
    total_amount_cents?: number | null;
    currency_code?: string;
    line_items?: Array<Record<string, unknown>>;
    activation_cost?: number | null;
    energy_cost?: number | null;
    charging_cost?: number | null;
    idle_cost?: number | null;
    minimum_charge_cost?: number | null;
    estimate_status?: "priced" | "free" | "unavailable" | "unsupported";
    unsupported_components?: string[];
  };
  // persisted | legacy_recomputed | pending | failed | unavailable
  billing_source?: string;
  // The row's billing_status; distinguishes not_authorized inside "unavailable".
  billing_status?: string | null;
  [key: string]: unknown;
}

export interface LBGroup {
  group_id: string;
  name: string;
  max_amps: number;
  min_amps: number;
  unit_ids: string[];
  [key: string]: unknown;
}

export interface LBGroupStatus {
  group_id: string;
  name: string;
  max_amps: number;
  total_current_draw: number;
  headroom: number;
  utilization_pct: number;
  last_recalculation?: string;
  phantom_count?: number;
  phantom_ids?: string[];
  units: Array<{
    unit_id: string;
    name: string;
    current_draw: number;
    allocated_amps: number;
    allocated?: number;
    is_online: boolean;
    state_name: string;
  }>;
  [key: string]: unknown;
}

export interface EventLogEntry {
  timestamp: string;
  unit_id: string;
  event_code: number;
  event_name?: string;
  energy_wh?: number;
  duration_seconds?: number;
  [key: string]: unknown;
}


export type StatsResponseDto = {
  messages_per_second?: number;
  wups_per_second?: number;
  active_units?: number;
  unsupported_units?: number;
  state_distribution?: Record<string, number>;
  // Fleet protocol-version breakdown (admin snapshot only) — feeds the
  // Devices page protocol filter options. Keys are strings ("0", "9").
  protocol_counts?: Record<string, number>;
};


export interface MapLocation {
  ip: string;
  lat: number;
  lon: number;
  city: string;
  state: string;
  country: string;
  isp: string;
  unit_count: number;
  online_count: number;
  offline_count: number;
  status: "online" | "offline" | "mixed" | "no_units";
  units: Array<{ unit_id: string; name: string; is_online: boolean; state_name: string }>;
}

/* ------------------------------------------------------------------ */
/*  Zod Schemas (units)                                                */
/* ------------------------------------------------------------------ */

export const UnitSchema = z.object({
  unit_id: z.string(),
  name: z.string().catch(""),
  state_name: z.string().nullable().optional(),
  state: z.any().optional(),
  ip_address: z.string().optional().nullable(),
  mac_address: z.string().optional().nullable(),
  is_online: z.boolean().optional(),
  current: z.number().optional().nullable(),
  voltage: z.number().optional().nullable(),
  power_kw: z.number().optional().nullable(),
  work_mode: z.string().optional().nullable(),
  pricing_mode: z.string().optional().nullable(),
  charging_permitted: z.boolean().optional().nullable(),
  customer_self_service_enabled: z.boolean().optional().nullable(),
  timezone: z.string().optional().nullable(),
  frequency: z.number().optional().nullable(),
  power_factor: z.number().optional().nullable(),
  session_energy: z.number().optional().nullable(),
  temperature: z.number().optional().nullable(),
  wifi_rssi: z.number().optional().nullable(),
  protocol_version: z.any().optional().nullable(),
  online_current_limit: z.number().optional().nullable(),
  offline_current_limit: z.number().optional().nullable(),
  unit_rating: z.number().optional().nullable(),
  // False when the unit:meta config mirror is absent in Redis, so name/limits
  // fell back to blank/0. The UI shows "No data" instead of a misleading 0 A.
  // Absent/undefined is treated as available (legacy responses, projection rows).
  config_available: z.boolean().optional(),
  // False when the unit:tel telemetry mirror is absent (TTL-expired — unit
  // offline > ~5 min), so voltage/current/… are placeholder zeros, not real
  // readings. The UI shows "—" instead. Absent/undefined = available (legacy).
  telemetry_available: z.boolean().optional(),
  effective_current_limit: z.number().optional().nullable(),
  tou_enabled: z.boolean().optional().nullable(),
  tou_schedules: z.array(z.object({
    day: z.string(),
    start: z.string(),
    end: z.string(),
  })).optional().nullable(),
  tou: z.object({
    enabled: z.boolean(),
    currently_allowed: z.boolean(),
    schedules: z.array(z.object({ day: z.string(), start: z.string(), end: z.string() })),
    timezone: z.string(),
    current_window_end: z.string().nullable(),
    next_window_start: z.string().nullable(),
    next_window_end: z.string().nullable(),
  }).optional().nullable(),
  // Live session projection (the Redis unit:session mirror). `start_gate` is the
  // orchestrator's verdict — the dashboard PROJECTS it (e.g. renders "Stopped"),
  // it never re-derives charging permission from it.
  current_session: z.object({
    session_id: z.string().optional().nullable(),
    start_gate: z.string().optional().nullable(),
    is_active: z.boolean().optional().nullable(),
    session_ended: z.any().optional(),
  }).passthrough().optional().nullable(),
  lifetime_energy: z.number().optional().nullable(),
  last_seen: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
  location_isp: z.string().optional().nullable(),
  // Station info (Mongo migration). Null for native master2 units.
  station_name: z.string().optional().nullable(),
  station_address: z.string().optional().nullable(),
  station_latitude: z.number().optional().nullable(),
  station_longitude: z.number().optional().nullable(),
  station_time_zone: z.string().optional().nullable(),
  // Organization the unit belongs to (or null if not in any org).
  organization: z.object({
    id: z.number(),
    name: z.string(),
  }).optional().nullable(),
  // Load-balancing circuit group this unit belongs to (or null if not in any
  // group / LB disabled). Drives the badge on the unit page linking to the
  // group's settings on /load-balancing.
  load_balancing_group: z.object({
    group_id: z.string(),
    name: z.string(),
  }).optional().nullable(),
});
export type Unit = z.infer<typeof UnitSchema>;

export const UnitsResponseSchema = z.object({
  units: z.array(UnitSchema),
  total: z.number(),
  no_units_assigned: z.boolean().optional(),
  // The list could not be read (fleet index not live / Redis error): units is
  // empty because of that, not because there are none. Never cached server-side.
  degraded: z.boolean().optional(),
  degraded_reason: z.string().optional(),
  index_unavailable_reason: z.string().optional(),
  // Fleet pagination: a page is at most page_limit rows; truncated means the
  // limit (not `end`) cut it — continue from next_start.
  start: z.number().optional(),
  page_limit: z.number().optional(),
  truncated: z.boolean().optional(),
  next_start: z.number().nullable().optional(),
});
export type UnitsResponse = z.infer<typeof UnitsResponseSchema>;

/* ------------------------------------------------------------------ */
/*  Auth                                                               */
/* ------------------------------------------------------------------ */

export async function fetchCurrentUser(): Promise<{ user: AuthUser }> {
  return http<{ user: AuthUser }>("/api/auth/me");
}

/** PUBLIC: which external login/registration options the login page may offer
 *  (queried before a session exists). */
export type PhoneRegistrationMode = "disabled" | "invite_only" | "open";

export interface AuthProviders {
  google_login: boolean;
  phone_registration?: boolean;
  phone_registration_mode?: PhoneRegistrationMode;
}

export async function fetchAuthProviders(): Promise<AuthProviders> {
  return http<AuthProviders>("/api/auth/providers");
}

/** PUBLIC: request a registration SMS code for an invited phone. `status` is
 *  "sent" | "exists" | "not_invited" | "not_sent". */
export async function requestRegistrationSms(phone: string) {
  return http<{ status: string; message: string }>("/api/public/auth/register/request-sms", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
}

/** PUBLIC: confirm registration with the SMS code + a password (username
 *  optional; defaults to the phone). `logged_in` says whether the browser was
 *  signed in to the new account. */
export async function confirmRegistration(payload: {
  phone: string; code: string; password: string; username?: string;
}) {
  // `invitation_status` reports the org-join outcome separately from account
  // creation: "joined" (normal), "failed" (account exists, but no org join was
  // confirmed - it may not have happened or its outcome may be unknown), "none"
  // (nothing left to consume).
  return http<{
    success: boolean;
    user_id: number;
    username: string;
    /** True only when the server signed this browser in to the new account. */
    logged_in: boolean;
    joined_organizations: number[];
    invitation_status?: "joined" | "failed" | "none";
    message?: string;
  }>(
    "/api/public/auth/register/confirm",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

/** Unlink Google from the current account. Rejected (400) if Google is the
 *  account's only sign-in method. */
export async function disconnectGoogle(): Promise<{ success: boolean }> {
  return http<{ success: boolean }>("/api/auth/google/disconnect", { method: "POST" });
}

/* ------------------------------------------------------------------ */
/*  Units                                                              */
/* ------------------------------------------------------------------ */

export async function fetchUnits(args: {
  start: number;
  end: number;
  search?: string;
  state?: string;
  sort?: string;
  mode?: string;
  pricing?: string;
  // Comma list of protocol versions ("0,9") — admin-only UI, harmless otherwise.
  protocol?: string;
  // M:N — an organization MANAGER passes their selected org so the request is the
  // org-scoped endpoint (/api/org/<id>/units). System admin (or no manager org)
  // omits it and uses the fleet-wide endpoint. There is no singular org fallback.
  orgId?: number | null;
  // Why this request is made (poll / ws / reconnect / user …), counted
  // server-side to see how the fleet list is being refreshed.
  refreshSource?: string;
}): Promise<UnitsResponse> {
  const base = args.orgId != null ? `/api/org/${args.orgId}/units` : "/api/units";
  const url = new URL(base, window.location.origin);
  url.searchParams.set("start", String(args.start));
  url.searchParams.set("end", String(args.end));
  if (args.search) url.searchParams.set("search", args.search);
  if (args.state) url.searchParams.set("state", args.state);
  if (args.sort) url.searchParams.set("sort", args.sort);
  if (args.mode) url.searchParams.set("mode", args.mode);
  if (args.pricing) url.searchParams.set("pricing", args.pricing);
  if (args.protocol) url.searchParams.set("protocol", args.protocol);

  const init = args.refreshSource ? { headers: { "X-Refresh-Source": args.refreshSource } } : {};
  const data = await http<unknown>(url.toString(), init);
  return UnitsResponseSchema.parse(data);
}

export async function fetchUnit(unitId: string): Promise<Unit> {
  const data = await http<unknown>(`/api/unit/${encodeURIComponent(unitId)}`);
  return UnitSchema.parse(data);
}

export async function updateUnitControl(unitId: string, patch: { name?: string; online_current?: number | null; offline_current?: number | null }) {
  return http<{ success: boolean }>(`/api/unit/${encodeURIComponent(unitId)}/control`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

/**
 * Atomic partial update of unit settings (P0-3). Backed by
 * PATCH /api/unit/<id>/settings, which persists to PG and publishes ONE
 * cmd:settings_update envelope so the udp side applies all fields in
 * publish order. Prefer this over the per-field POSTs below whenever the
 * caller knows it might change two or more fields close in time.
 */
export interface UnitSettingsPatch {
  name?: string;
  online_current_limit?: number;
  work_mode?: "plug-and-charge" | "commercial-public";
  customer_self_service_enabled?: boolean;
  charging_permitted?: boolean;
  timezone?: string;
  tou_enabled?: boolean;
  tou_schedules?: Array<{ day: string; start: string; end: string }>;
  pricing_mode?: "free" | "paid";
  pricebook?: unknown;
}

export async function patchUnitSettings(unitId: string, patch: UnitSettingsPatch) {
  return http<{ success: boolean; queued?: boolean; fields?: string[] }>(
    `/api/unit/${encodeURIComponent(unitId)}/settings`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

export async function fetchStats(orgId?: number | null): Promise<StatsResponseDto> {
  return http<StatsResponseDto>(orgId != null ? `/api/org/${orgId}/stats` : `/api/stats`);
}

export async function unitHeartbeat(unitId: string) {
  return http<{ success: boolean }>(`/api/unit/${encodeURIComponent(unitId)}/heartbeat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ts: Date.now() }),
  });
}

export async function permitCharging(unitId: string) {
  return http<any>(`/api/unit/${encodeURIComponent(unitId)}/permit-charging`, { method: "POST" });
}

/** Result of a manual stop/resume — a projection of the orchestrator's verdict.
 *  `start_gate` is authoritative: on resume it may come back as something other
 *  than "permitted" (e.g. blocked_tou), and the UI must show that, not assume
 *  the requested outcome. */
export type ManualStopResult = {
  session_id: string;
  start_gate: string;
  permitted: boolean;
  stopped: boolean;
  manual_stop_active: boolean;
  manual_stop_at: string | null;
  manual_stop_source: string | null;
  /** Present for paid sessions — reported, never routed on. */
  payment_status?: string | null;
  payment_required?: boolean;
};

/** Pause the unit's ACTIVE charging session — any work mode, paid or free.
 *  Session-scoped: the stop lives on the session row, so it dies with the
 *  session and never carries into the next plug-in. The session is not
 *  finalized and no payment is captured; the paused interval bills as idle. */
export async function stopSessionCharging(unitId: string) {
  return http<ManualStopResult>(
    `/api/unit/${encodeURIComponent(unitId)}/session/charging-stop`,
    { method: "POST" },
  );
}

/** Resume a session stopped via stopSessionCharging. The backend re-runs the
 *  full permit pipeline rather than forcing a permit. */
export async function resumeSessionCharging(unitId: string) {
  return http<ManualStopResult>(
    `/api/unit/${encodeURIComponent(unitId)}/session/charging-resume`,
    { method: "POST" },
  );
}

export async function updateTimezone(unitId: string, timezone: string) {
  return http<any>(`/api/unit/${encodeURIComponent(unitId)}/timezone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timezone }),
  });
}

/* ------------------------------------------------------------------ */
/*  Unit — Work Mode                                                   */
/* ------------------------------------------------------------------ */

export async function setWorkMode(unitId: string, work_mode: "plug-and-charge" | "commercial-public") {
  return http<{ success: boolean }>(`/api/unit/${encodeURIComponent(unitId)}/work-mode`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ work_mode }),
  });
}

/* ------------------------------------------------------------------ */
/*  Unit — Customer Self-Service                                       */
/* ------------------------------------------------------------------ */

export async function setCustomerSelfService(unitId: string, enabled: boolean) {
  return http<{ success: boolean; customer_self_service_enabled: boolean }>(`/api/unit/${encodeURIComponent(unitId)}/customer-self-service`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
}

/* ------------------------------------------------------------------ */
/*  Unit — TOU                                                         */
/* ------------------------------------------------------------------ */

export async function updateTou(unitId: string, tou_enabled: boolean, tou_schedules: Array<{ day: string; start: string; end: string }>) {
  return http<any>(`/api/unit/${encodeURIComponent(unitId)}/tou`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tou_enabled, tou_schedules }),
  });
}

/* ------------------------------------------------------------------ */
/*  Unit — Tariff                                                      */
/* ------------------------------------------------------------------ */

export type FeeType = "charging" | "idle" | "activation";
export type Uom = "kwh" | "hour" | "session";

/** One pricing rule. TOU fields are optional and only apply when is_tou. */
export interface RateRule {
  fee_type: FeeType;
  uom: Uom;
  rate_base: number;
  is_tou?: boolean;
  start_time?: string; // "HH:MM"
  end_time?: string;   // "HH:MM"
  days?: string;       // CSV of 0..6 (Sun=0); "" = all days
  priority?: number;
}

export interface Pricebook {
  currency_code: string;
  effective_pricebook: RateRule[];
}

export interface TariffConfig {
  pricing_mode: "free" | "paid";
  currency: string;
  energy_rate: number;
  hourly_rate: number;
  idle_fee: number;
  activation_fee: number;
  /** Full canonical pricebook (advanced mode). Flat fields above are derived. */
  pricebook?: Pricebook;
  /** False when the unit:meta config mirror is absent, so the tariff fell back
   *  to free/empty. The editor shows "No data" instead of a misleading FREE. */
  config_available?: boolean;
}

/* Route families F1 (unit sessions), F3 (unit billing totals), F6 (unit
 * tariff), F7 (pricing-profile catalog), F8 (org sessions report JSON) and F14 (unit
 * pricing context) use money contract 2. F6 and F7 are read and written with the exact
 * persisted tariff, its server currency choices, the tenant's operating currency, and no
 * legacy money key outside the USD contour. F1, F3, F8 and F14 are read only, from their
 * money_v2 siblings. Only these
 * helpers opt in; every other route keeps its legacy contract. A body that
 * breaks the contract is returned as an `invalid` read, never as a legacy USD
 * value. */
const MONEY_CONTRACT_2 = "money_contract=2";

/** The F6 management view of a unit's persisted tariff. */
export async function fetchTariff(unitId: string): Promise<ManagementRead<TariffManagementV2>> {
  return readTariffResponseV2(await http<unknown>(`/api/unit/${encodeURIComponent(unitId)}/tariff?${MONEY_CONTRACT_2}`));
}

/* Charger pricing context (F14, manager/admin-only): the unit's persisted tariff and
 * every organization-wide pricing-profile assignment with its profile's exact tariff.
 * Applicability + precedence come from the backend (the org pricing resolver's
 * specificity); the frontend never recomputes them. */
export async function fetchPricingContext(unitId: string): Promise<ManagementRead<PricingContextV2>> {
  return readPricingContextResponseV2(await http<unknown>(`/api/units/${encodeURIComponent(unitId)}/pricing-context?${MONEY_CONTRACT_2}`));
}

export async function updateTariff(unitId: string, tariff: TariffConfig) {
  return http<{ success: boolean; tariff: TariffConfig; pricing_mode: string }>(`/api/unit/${encodeURIComponent(unitId)}/tariff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(tariff),
  });
}

/** Save the full pricebook (advanced/rules mode), preserving TOU windows; the answer projects the tariff written. */
export async function updateTariffPricebook(
  unitId: string,
  pricing_mode: "free" | "paid",
  pricebook: PricebookWrite,
): Promise<ManagementRead<TariffManagementV2>> {
  return readTariffResponseV2(await http<unknown>(
    `/api/unit/${encodeURIComponent(unitId)}/tariff?${MONEY_CONTRACT_2}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pricing_mode, pricebook }),
    },
  ));
}

/* ------------------------------------------------------------------ */
/*  Unit — Sessions                                                    */
/* ------------------------------------------------------------------ */

/** F1 sessions with money contract 2; a body without the contract marker is an `invalid` read, a row with unreadable money keeps its other fields. */
export async function fetchUnitSessions(
  unitId: string,
  params?: { include_history?: boolean; limit?: number; offset?: number },
): Promise<UnitSessionsRead> {
  const url = new URL(`/api/unit/${encodeURIComponent(unitId)}/sessions?${MONEY_CONTRACT_2}`, window.location.origin);
  if (params?.include_history !== undefined) url.searchParams.set("include_history", String(params.include_history));
  if (params?.limit) url.searchParams.set("limit", String(params.limit));
  if (params?.offset) url.searchParams.set("offset", String(params.offset));
  return readUnitSessionsPage(await http<unknown>(url.toString()));
}

/* ------------------------------------------------------------------ */
/*  Unit — Billing                                                     */
/* ------------------------------------------------------------------ */

/** F3 totals with money contract 2; a body without the contract marker or a valid money_v2 is an `invalid` read. */
export async function fetchUnitBillingTotals(unitId: string): Promise<UnitBillingTotalsRead> {
  return readUnitBillingTotals(await http<unknown>(`/api/unit/${encodeURIComponent(unitId)}/billing/totals?${MONEY_CONTRACT_2}`));
}

/* ------------------------------------------------------------------ */
/*  Unit — Users (assignment)                                          */
/* ------------------------------------------------------------------ */

/** Why a user holds a unit (backend `user_units.assignment_kind`). Only `personal_owner` is
 *  ownership; generic assignment creates `delegated` access. */
export type AssignmentKind =
  | "personal_owner" | "organization_grant" | "delegated" | "quarantined" | "legacy";

export async function fetchUnitUsers(unitId: string) {
  return http<{ users: Array<{ id: number; username: string; role: string;
                               assignment_kind?: AssignmentKind | null; grant_org_id?: number | null }> }>(
    `/api/units/${encodeURIComponent(unitId)}/users`);
}

/** The charger's support tickets and warranty in Odoo, where admins sign in with their own
 * accounts (admins only). Both are null when there are none: Odoo not set up, or not a real
 * charger. */
export async function fetchOdooTicketsLink(unitId: string) {
  return http<{ url: string | null; warranty_url?: string | null }>(
    `/api/unit/${encodeURIComponent(unitId)}/odoo-tickets-link`,
  );
}

export async function assignUnitToUser(unitId: string, userId: number) {
  return http<any>(`/api/units/${encodeURIComponent(unitId)}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_ids: [userId] }),
  });
}

export async function removeUnitFromUser(unitId: string, userId: number) {
  return http<any>(`/api/units/${encodeURIComponent(unitId)}/users/${userId}`, { method: "DELETE" });
}

export const ClaimResultSchema = z.object({
  success: z.boolean(),
  unit_id: z.string(),
  already_owner: z.boolean(),
});
export type ClaimResult = z.infer<typeof ClaimResultSchema>;

/**
 * Residential self-claim: link an UNASSIGNED residential charger to the current
 * user. The server proves possession by the charger having reported from the
 * same public IP as this request (charger + device on one home NAT).
 *
 * 200 → ClaimResult. Non-2xx throws HttpError; callers branch on
 * `err.body.error` (e.g. "already_claimed" carries `owner_email_masked`,
 * "ip_mismatch", "unit_not_found", …). See docs/api/unit-claim.md and
 * contracts/unit_claim.schema.json.
 */
export async function claimUnit(unitId: string): Promise<ClaimResult> {
  const data = await http<unknown>(`/api/unit/${encodeURIComponent(unitId)}/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  return ClaimResultSchema.parse(data);
}

/* ------------------------------------------------------------------ */
/*  Sessions (global)                                                  */
/* ------------------------------------------------------------------ */

export async function fetchActiveSessions(params?: {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  search?: string;
  orgId?: number | null;
}): Promise<ActiveSessionsResponse> {
  const base = params?.orgId != null
    ? `/api/org/${params.orgId}/sessions/active` : "/api/sessions/active";
  const url = new URL(base, window.location.origin);
  if (params?.limit) url.searchParams.set("limit", String(params.limit));
  if (params?.offset) url.searchParams.set("offset", String(params.offset));
  if (params?.sort_by) url.searchParams.set("sort_by", params.sort_by);
  if (params?.sort_order) url.searchParams.set("sort_order", params.sort_order);
  if (params?.search) url.searchParams.set("search", params.search);
  return http<ActiveSessionsResponse>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Billing                                                            */
/* ------------------------------------------------------------------ */

/** One currency's amount. `amount` is `amount_cents` in major units; currencies are never
 *  converted or summed together. */
export interface BillingCurrencyAmount {
  currency: string;
  amount: number;
  amount_cents: number;
  sessions: number;
}

export interface BillingFinalizedSource {
  source: string;
  currency: string;
  amount_cents: number;
  sessions: number;
}

export interface BillingUnresolvedDetail {
  reason: string;
  billing_status: string | null;
  sessions: number;
}

export interface BillingTotalsWindow {
  frozen_at: string;
  completed_start: string | null;
  completed_basis: string;
  active_since: string | null;
  active_basis: string;
}

export interface BillingTotalsRefreshError {
  reason: string;
  retryable: boolean;
  failures?: number;
  last_failed_at?: string | null;
  retry_after_seconds?: number;
}

export type ActiveEstimateStatus = "estimated" | "unavailable" | "no_active_session";

/**
 * Billing totals of one scope (fleet, org, user or unit), served from a cache the backend
 * refreshes in the background. Every scalar is null until the first pass publishes (cold),
 * and stays null where the value is unknown, so null never means 0.
 *
 * Finalized billed amounts and active-session estimates are separate figures; neither is
 * money collected. `total_revenue` is a number only for a known, complete finalized total in
 * ONE currency; otherwise it is null and `finalized_billed` holds the known per-currency
 * subtotals beside `unresolved_billing_count`.
 */
export interface BillingTotals {
  scope: string;
  period: "all" | "month";
  window: BillingTotalsWindow | null;
  computing: boolean;
  stale: boolean;
  complete: boolean;
  cache_updated_at: string | null;
  cache_age_seconds: number | null;
  progress: { refresh_queued: boolean; pass: unknown; published_generation?: number } | null;
  refresh_error: BillingTotalsRefreshError | null;

  completed_sessions: number | null;
  completed_energy_kwh: number | null;
  completed_energy_unknown_sessions: number | null;
  finalized_billed: BillingCurrencyAmount[];
  finalized_sources: BillingFinalizedSource[];
  unresolved_billing_count: number | null;
  unresolved_by_reason: Record<string, number>;
  unresolved_detail: BillingUnresolvedDetail[];
  finalized_revenue: number | null;
  revenue_currency: string | null;
  total_revenue: number | null;

  active_sessions: number | null;
  active_energy_kwh: number | null;
  active_energy_unknown_sessions: number | null;
  active_estimated: BillingCurrencyAmount[];
  active_estimate_unknown_count: number | null;
  active_estimated_revenue: number | null;
  active_estimate_currency: string | null;
  active_estimate_status: ActiveEstimateStatus | null;
  active_deduplicated_completed: number | null;

  total_sessions: number | null;
  total_energy_kwh: number | null;
  /** Distinct units with a completed session in scope; null when unknown, never the scope size. */
  total_chargers: number | null;

  /** Only on the empty-scope bodies (org without units, user without assigned units). */
  total_charging_hours?: number;
  units_with_data?: number;
  no_units_assigned?: boolean;
}

export async function fetchBillingTotals(period?: string, orgId?: number | null) {
  const base = orgId != null ? `/api/org/${orgId}/billing/totals` : "/api/billing/system_totals";
  const url = period ? `${base}?period=${period}` : base;
  return http<BillingTotals>(url);
}

/* ------------------------------------------------------------------ */
/*  Users                                                              */
/* ------------------------------------------------------------------ */

export async function fetchUsers(opts: {
  q?: string;
  cursor?: number;
  limit?: number;
  role?: SystemRole;
  emailStatus?: EmailStatusFilter;
  plan?: PlanFilter;
} = {}): Promise<UsersPage> {
  const params = new URLSearchParams();
  if (opts.q) params.set("q", opts.q);
  if (opts.cursor) params.set("cursor", String(opts.cursor));
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.role) params.set("role", opts.role);
  if (opts.emailStatus) params.set("email_status", opts.emailStatus);
  if (opts.plan) params.set("plan", opts.plan);
  const qs = params.toString();
  return http<UsersPage>(`/api/users${qs ? `?${qs}` : ""}`);
}

/** Aggregate account counts for the Users-page header (admin only).
 *  Served from the server's in-memory cache with a short TTL — cheap to call
 *  once per page load. `active_users` = accounts that have logged in at least
 *  once (NOT online chargers). Contract: contracts/users_stats.schema.json. */
export type UserStats = {
  total_users: number;
  active_users: number;
  never_logged_in: number;
  disabled: number;
  admins: number;
  // Distinct users currently holding a live credential (credential presence,
  // not "actively viewing"): logged_in_now = union of web + app.
  logged_in_now: number;
  logged_in_web: number;
  logged_in_app: number;
};

export async function fetchUserStats(): Promise<UserStats> {
  return http<UserStats>("/api/users/stats");
}

export async function createUser(payload: { username: string; password: string; role: SystemRole; email?: string }) {
  return http<any>("/api/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function deleteUser(userId: number) {
  return http<any>(`/api/users/${userId}`, { method: "DELETE" });
}

/** Single user, enriched like a users-list row. Powers the deep-linkable User
 *  Details page (e.g. clicking a member on an organization's page). Admin only. */
export async function fetchUser(userId: number) {
  return http<UserRecord>(`/api/users/${userId}`);
}

export async function changePassword(userId: number, password: string) {
  return http<{ success: boolean }>(`/api/users/${userId}/password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export async function changeRole(userId: number, role: SystemRole) {
  return http<{ success: boolean }>(`/api/users/${userId}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
}

/** Give a user free Prime for `days` days (admin only). The grant self-expires. */
export async function grantPrime(userId: number, days: number) {
  return http<{ success: boolean; prime_until: string; prime_grant_active: boolean; tier: string }>(
    `/api/users/${userId}/prime-grant`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    },
  );
}

/** Revoke a user's temporary free Prime grant (admin only). */
export async function revokePrime(userId: number) {
  return http<{ success: boolean; tier: string }>(`/api/users/${userId}/prime-grant`, {
    method: "DELETE",
  });
}

export async function setUserDisabled(userId: number, disabled: boolean) {
  return http<{ success: boolean; disabled: boolean }>(`/api/users/${userId}/disabled`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ disabled }),
  });
}

export async function unlockUser(userId: number) {
  return http<{ success: boolean; cleared: boolean }>(`/api/users/${userId}/unlock`, {
    method: "POST",
  });
}

export interface ContactUpdateResult {
  success: boolean;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
}

/** Set/clear a user's email and/or phone (admin). Only keys present are
 *  changed; an empty string clears. Admin-set contacts default to verified. */
export async function setUserContact(
  userId: number,
  contact: { email?: string; phone?: string; email_verified?: boolean; phone_verified?: boolean },
) {
  return http<ContactUpdateResult>(`/api/users/${userId}/contact`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contact),
  });
}

export type ResetChannel = "email" | "sms";

/** Authoritative recovery-method availability for the reset UI (backend applies
 *  the full eligibility rule incl. E.164 + allowed country). Only MASKED
 *  destinations are returned. */
export interface ResetAvailability {
  email_reset_available: boolean;
  sms_reset_available: boolean;
  masked_email: string | null;
  masked_phone: string | null;
  sms_unavailable_reason: string | null;
}

/** Admin: recovery-method availability for the selected user. */
export async function fetchUserResetAvailability(userId: number) {
  return http<ResetAvailability>(`/api/users/${userId}/reset-availability`);
}

/** Transactional-SMS consent state for the current user's Account Settings. */
export interface SmsPreferences {
  /** SMS reset channel kill switch. When false the backend returns ONLY this
   *  field and the opt-in section is hidden. */
  feature_enabled?: boolean;
  masked_phone: string | null;
  phone_verified: boolean;
  /** Whether the current phone can receive an opt-in at all. */
  opt_in_eligible: boolean;
  opt_in_ineligible_reason: string | null;
  /** Whether consent is currently active for the current number. */
  consent_active: boolean;
  /** Evidence version of exactly this disclosure; submitted back with an opt-in. */
  consent_text_version: string;
  /** Language of the disclosure the server chose (English unless the requested one is available). */
  disclosure_language: string;
  /** Exact server bytes to render; never translated in the client. */
  disclosure_text: string;
  /** Server-computed SHA-256 of disclosure_text. */
  disclosure_hash: string;
  privacy_policy_url: string;
  terms_of_service_url: string;
}

/** `language` asks for the disclosure in that language; the server answers English when it cannot show it. */
export async function fetchSmsPreferences(language: string) {
  return http<SmsPreferences>(`/api/account/sms-preferences?language=${encodeURIComponent(language)}`);
}

/** The client submits the desired opt-in state and, for an opt-in, the evidence
 *  version of the disclosure it displayed; the server derives phone, E.164,
 *  timestamp, IP, source and disclosure hash. */
export async function updateSmsPreferences(optIn: boolean, consentTextVersion?: string) {
  return http<{ success: boolean; consent_active: boolean }>(
    `/api/account/sms-preferences`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(optIn ? { opt_in: true, consent_text_version: consentTextVersion } : { opt_in: false }),
    },
  );
}

/** Self-service: recovery-method availability for the current authenticated user. */
export async function fetchAccountResetAvailability() {
  return http<ResetAvailability>(`/api/account/reset-availability`);
}

/** Admin-initiated password reset for a user over the chosen channel
 *  ('email' → one-time link, 'sms' → 6-digit OTP). The client sends ONLY the
 *  channel; the backend loads the destination from the record. Neutral response. */
export async function sendUserReset(userId: number, channel: ResetChannel = "email") {
  return http<{ success: boolean; requested: boolean }>(
    `/api/users/${userId}/send-reset`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel }),
    },
  );
}

/** Self-service: the current authenticated user requests a password reset over
 *  the chosen channel to their OWN recovery destination. Only the channel is
 *  sent; the account + destination come from the session. Neutral response. */
export async function requestAccountReset(channel: ResetChannel = "email") {
  return http<{ success: boolean; requested: boolean }>(
    `/api/account/send-reset`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel }),
    },
  );
}

/** Public: validate an SMS reset challenge reference (no consume). Sent in the
 *  POST body; opaque, so the reason reveals nothing about any account. */
export async function checkSmsChallenge(challenge: string) {
  return http<{ valid: boolean; reason: string }>(
    `/api/public/auth/reset-sms`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** Public: complete an SMS reset with the challenge reference + the 6-digit OTP
 *  + a new password. The OTP authorizes; the challenge only identifies the
 *  bound account. */
export async function confirmSmsReset(challenge: string, code: string, newPassword: string) {
  return http<{ success: boolean }>(
    `/api/public/auth/reset-sms/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge, code, new_password: newPassword }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** Public/anonymous: request a password-reset LINK by email (login "Forgot
 *  password?"). The backend ALWAYS answers 200 with the same generic message
 *  whether or not the email matches an account, so the caller can never use this
 *  to enumerate accounts. */
export async function requestAnonymousReset(email: string) {
  return http<{ success: boolean; message: string }>(
    `/api/public/auth/request-reset-email`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** Public: what the anonymous "Forgot password?" page may offer. The SMS option
 *  + its consent disclosure appear only while the SMS channel is enabled. */
export interface ResetOptions {
  email_enabled: boolean;
  sms_enabled: boolean;
  sms_consent_text: string;
  sms_consent_version: string;
  privacy_policy_url: string;
  terms_of_service_url: string;
}

export async function fetchResetOptions() {
  return http<ResetOptions>(`/api/public/auth/reset-options`);
}

/** Public/anonymous: request a password-reset CODE by SMS. The caller types a
 *  phone number and gives explicit consent on the form. Same generic 200 as the
 *  email path (no account enumeration). */
export async function requestAnonymousResetSms(phone: string, consent: boolean) {
  return http<{ success: boolean; message: string }>(
    `/api/public/auth/request-reset-sms`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, consent }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** Public: validate a password-reset link token (no consume). The token is sent
 *  in the POST body — never a query string — so it can't leak into access logs
 *  or a Referer. Opaque token, so the reason never reveals any account. */
export async function checkResetToken(token: string) {
  return http<{ valid: boolean; reason: string }>(
    `/api/public/auth/reset-token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** Public: set a new password using a reset-link token (bound to a user_id). */
export async function confirmPasswordReset(token: string, newPassword: string) {
  return http<{ success: boolean }>(
    `/api/public/auth/reset-password/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
      referrerPolicy: "no-referrer",
    },
  );
}

/** What a user can reach: org memberships + directly assigned units
 *  (the user→units/orgs mirror of /api/units/<id>/users). Admin only. */
export interface UserAccessOrg {
  organization_id: number;
  organization_name: string | null;
  role: string;
}
export interface UserAccessUnit {
  unit_id: string;
  name?: string;
  is_online?: boolean;
  state_name?: string;
}
export interface UserAccess {
  user_id: number;
  organizations: UserAccessOrg[];
  units: UserAccessUnit[];
}
export async function fetchUserAccess(userId: number) {
  return http<UserAccess>(`/api/users/${userId}/access`);
}

/* ------------------------------------------------------------------ */
/*  Logs                                                               */
/* ------------------------------------------------------------------ */

/** Telemetry columns of a log entry. Current writers nest them under
 *  `telemetry`; older entries carried them flat on the entry. */
export interface LogTelemetry {
  state_name?: string | null;
  voltage?: number | null;
  current?: number | null;
  frequency?: number | null;
  power_factor?: number | null;
  session_energy?: number | null;
  temperature?: number | null;
  wifi_rssi?: number | null;
}

export interface LogCommandDecoded {
  day?: string;
  time?: string;
  online_current?: number;
  offline_current?: number;
  rapid_mode?: boolean;
  flags?: number;
  server_index?: number;
}

export interface LogEntry extends LogTelemetry {
  timestamp?: string | null;
  unit_id?: string | null;
  raw_message?: string | null;
  command?: string | null;
  command_decoded?: LogCommandDecoded | null;
  telemetry?: LogTelemetry | null;
  [key: string]: unknown;
}

/** Stable codes; null means the writer heartbeat is fresh. */
export type LogsWriterReason =
  | "disabled_by_config"
  | "heartbeat_missing"
  | "heartbeat_stale"
  | "heartbeat_malformed"
  | "redis_unavailable"
  | "writes_failing"
  | "store_config_rejected";

/** The UDP-side diag:logs mirror, as its heartbeat reports it. Counters are
 *  null when unknown, never an invented zero. */
export interface LogsWriterStatus {
  enabled: boolean;
  reason: LogsWriterReason | string | null;
  heartbeat_age_s: number | null;
  entries_written: number | null;
  dropped_entries: number | null;
  /** True while the writer's latest flush failed (alive, entries not stored);
   *  null when the writer does not report it. Absent on older servers. */
  write_error_active?: boolean | null;
  write_failures?: number | null;
  /** Seconds since the last successful write; null when none or unknown. */
  last_write_ok_age_s?: number | null;
  pending?: number | null;
}

/** Where the log entries are stored and that Redis server's own counters.
 *  Counters are null when unknown (metadata unavailable), never an invented zero. */
export interface LogsStorage {
  /** `dedicated`: a Redis of its own (DIAG_LOG_REDIS_URL); `live`: the live Redis. */
  mode: "live" | "dedicated" | string;
  metadata: "ok" | "unavailable" | string;
  /** Keys of every kind this Redis evicted since it started. Not a count of log messages. */
  evicted_keys: number | null;
  used_memory_bytes: number | null;
  /** 0 = no memory limit configured; null = limit unknown (never "unlimited"). */
  maxmemory_bytes: number | null;
  /** Fixed code of a rejected store configuration; never the configured value. */
  config_error?: string | null;
}

export interface LogsResponse {
  logs: LogEntry[];
  /** True only when the diagnostics store could not be read. */
  stale: boolean;
  reason: string | null;
  source: string;
  checked_at: string;
  /** Absent on servers that predate the writer projection. */
  newest_entry_age_s?: number | null;
  writer?: LogsWriterStatus | null;
  /** Absent on servers that predate the storage projection. */
  storage?: LogsStorage | null;
}

export async function fetchLogs(params: { unit_id?: string; limit?: number }) {
  const url = new URL("/api/logs", window.location.origin);
  if (params.unit_id) url.searchParams.set("unit_id", params.unit_id);
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  return http<LogsResponse>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Audit log (admin)                                                  */
/* ------------------------------------------------------------------ */

/**
 * The audit log records mutating API calls, and every request to the audit log
 * itself; other reads and charger protocol traffic never appear. Views are left
 * out of answers unless `include_views` is set. Every response carries `caveats`, which the UI shows
 * verbatim: they are facts the numbers cannot convey (counts are a lower
 * bound, src_ip is unreliable before the nginx fix, and so on).
 */
export interface AuditEntry {
  ts: string;
  method: string | null;
  path: string | null;
  // Optional: the API omits BOTH query and body unless ?include_bodies=1,
  // so on the default request neither is present. Declared required, any future
  // entry.query.includes(...) is a runtime TypeError tsc cannot see.
  query?: string;
  // Set only on the writer's fallback row, which carries ts and error and
  // nothing else — without it such a row renders as all dashes with no reason.
  error?: string | null;
  endpoint: string | null;
  status: number | null;
  actor: string;
  actor_kind: "user" | "label" | "none";
  actor_id: number | null;
  actor_username: string | null;
  actor_role: string | null;
  actor_label: string | null;
  src_ip: string | null;
  /** Present only when include_bodies was requested; holds personal data. */
  body?: unknown;
  body_truncated: boolean;
}

export interface AuditCoverageDay {
  day: string;
  /**
   * false: no recorded data that day — no changes, or the log was not written;
   * the log cannot tell which. null: the directory could not be listed.
   */
  recorded: boolean | null;
  entries: number;
  /** The window clips this day. `entries` is still the whole day's count. */
  partial: boolean;
}

/** Every day of the window, unfiltered: it describes the log, not the table. */
export interface AuditCoverage {
  days: AuditCoverageDay[];
  days_recorded: number;
  directory_ok: boolean;
  logging_enabled: boolean;
  note: string;
}

/**
 * One page, newest first. There is no total: pages are read on demand.
 * `next_cursor` continues the walk (null once the range is read through);
 * `searched_to` is set when a page was cut short by the server's read budget,
 * and says how far back it got.
 */
export interface AuditSearchResponse {
  limit: number;
  entries: AuditEntry[];
  next_cursor: string | null;
  searched_to: string | null;
  coverage: AuditCoverage;
  caveats: string[];
}

export interface AuditQuery {
  from?: string;
  to?: string;
  /** Everything kept, from the first recorded day to now, instead of from/to. */
  all?: boolean;
  actor?: string;
  role?: string;
  endpoint?: string;
  path?: string;
  method?: string;
  status?: string;
  ip?: string;
  anonymous?: boolean;
  limit?: number;
  cursor?: string;
  include_bodies?: boolean;
  include_views?: boolean;
}

function auditUrl(path: string, q: AuditQuery): string {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    // URLSearchParams escapes '+' in an ISO offset. It must: an unescaped '+'
    // arrives as a space, which the API refuses as not ISO-8601.
    url.searchParams.set(k, v === true ? "1" : String(v));
  }
  return url.toString();
}

export async function fetchAudit(q: AuditQuery = {}) {
  return http<AuditSearchResponse>(auditUrl("/api/audit", q));
}


/* ------------------------------------------------------------------ */
/*  Metrics                                                            */
/* ------------------------------------------------------------------ */

export interface MetricsHistoryResponse {
  metrics: any;
  stats: any;
  stale: boolean;
  reason: string | null;
  source: string;
  checked_at: string;
  generated_at?: string;
}

export async function fetchMetricsHistory(durationMinutes: number) {
  const url = new URL("/api/metrics/history", window.location.origin);
  url.searchParams.set("duration", String(durationMinutes));
  return http<MetricsHistoryResponse>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Response cache stats                                               */
/* ------------------------------------------------------------------ */

export interface CacheStats {
  enabled: boolean;
  hits: number;
  misses: number;
  bypassed: number;
  hit_rate: number;
  backend: "disabled" | "redis" | "memory";
}

export async function fetchCacheStats() {
  return http<CacheStats>("/api/cache/stats");
}

/* ------------------------------------------------------------------ */
/*  Mobile client versions                                            */
/* ------------------------------------------------------------------ */

export interface ClientVersionRow {
  platform: string;
  app_version: string;
  users: number;
}

export interface ClientVersionVolumeRow {
  day: string;
  platform: string;
  app_version: string;
  request_count: number;
}

export interface ClientVersionCollector {
  recorded: number;
  dropped: number;
  buffered: number;
  flushes_ok: number;
  flushes_failed: number;
  last_flush_ms: number;
  last_flush_rows: number;
}

export interface ClientVersionStats {
  active_window_days: number;
  volume_days: number;
  active_distribution: ClientVersionRow[];
  daily_volume: ClientVersionVolumeRow[];
  collector: ClientVersionCollector | null;
  policy: Record<string, unknown>;
  error?: string;
}

export async function fetchClientVersionStats(windowDays = 30) {
  const url = new URL("/api/admin/client-versions", window.location.origin);
  url.searchParams.set("window_days", String(windowDays));
  return http<ClientVersionStats>(url.pathname + url.search);
}

/* ------------------------------------------------------------------ */
/*  Map                                                                */
/* ------------------------------------------------------------------ */

export async function fetchMapLocations(limit = 100, offset = 0) {
  const url = new URL("/api/map/locations", window.location.origin);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));
  return http<{
    locations: MapLocation[];
    total_units: number;
    total_locations: number;
    total_unique_ips: number;
    resolved_ips: number;
    pending_ips: number;
  }>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  ECache                                                             */
/* ------------------------------------------------------------------ */

export async function fetchEcache(unitId?: string) {
  const url = new URL("/api/ecache", window.location.origin);
  if (unitId) url.searchParams.set("unit_id", unitId);
  return http<any>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Event Log                                                          */
/* ------------------------------------------------------------------ */

export async function fetchEventLog(params?: { unit_id?: string; start_time?: string; end_time?: string }) {
  const url = new URL("/api/eventlog", window.location.origin);
  if (params?.unit_id) url.searchParams.set("unit_id", params.unit_id);
  if (params?.start_time) url.searchParams.set("start_time", params.start_time);
  if (params?.end_time) url.searchParams.set("end_time", params.end_time);
  return http<any>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Power Heatmap                                                      */
/* ------------------------------------------------------------------ */

export interface HeatmapCell { x: string; y: number }
export interface HeatmapSeries { name: string; data: HeatmapCell[] }
export interface PowerHeatmapResponse {
  series: HeatmapSeries[];
  days: number;
  max_value: number;
}

export async function fetchPowerHeatmap(days: number, tz?: string, orgId?: number | null): Promise<PowerHeatmapResponse> {
  const tzParam = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const base = orgId != null ? `/api/org/${orgId}/power-heatmap` : "/api/power-heatmap";
  return http<PowerHeatmapResponse>(`${base}?days=${days}&tz=${encodeURIComponent(tzParam)}`);
}

/* ------------------------------------------------------------------ */
/*  Organizations                                                      */
/* ------------------------------------------------------------------ */

export interface Organization {
  id: number;
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  paid_until: string | null;
  comment: string;
  created_at: string;
  user_count: number;
  unit_count: number;
  // Total power capacity = sum of every owned charger's nameplate max current.
  // total_capacity_kw = total_capacity_amps * nominal_line_voltage / 1000.
  // Present when the server has a unit reader wired (both list and detail).
  total_capacity_amps?: number;
  total_capacity_kw?: number;
  nominal_line_voltage?: number;
  // Live (instantaneous) usage, present alongside capacity when a unit reader is
  // wired. online_unit_count = chargers currently online (vs. unit_count total);
  // current_power_* = what they're drawing right now (0 for an idle/offline org).
  online_unit_count?: number;
  current_power_kw?: number;
  current_power_amps?: number;
  // Optional fields imported from MongoDB (only present on Postgres backend)
  account_custom_id?: string | null;
  stripe_connect_account_id?: string | null;
  stripe_payout_enabled?: boolean | null;
  payout_schedule_preference?: string | null;
  revenue_percentage?: string | null;
}

export interface OrganizationUnitSummary {
  unit_id: string;
  name?: string | null;
  is_online?: boolean;
  state_name?: string | null;
  work_mode?: string | null;
  pricing_mode?: "free" | "paid" | null;
  unit_rating?: number; // nameplate max current (A)
}

export interface OrganizationDetail extends Organization {
  users: Array<{ id: number; username: string; role: SystemRole; org_role: "manager" | "driver" | null; tier: string }>;
  unit_ids: string[];
  units?: OrganizationUnitSummary[];
}

export async function fetchOrganizations(): Promise<{ organizations: Organization[] }> {
  return http<{ organizations: Organization[] }>("/api/organizations");
}

export async function fetchOrganization(orgId: number): Promise<{ organization: OrganizationDetail }> {
  return http<{ organization: OrganizationDetail }>(`/api/organizations/${orgId}`);
}

export async function createOrganization(payload: {
  name: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  paid_until?: string;
  comment?: string;
}) {
  return http<{ organization: Organization }>("/api/organizations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateOrganization(orgId: number, payload: {
  name?: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  paid_until?: string;
  comment?: string;
}) {
  return http<{ success: boolean }>(`/api/organizations/${orgId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function deleteOrganization(orgId: number) {
  return http<{ success: boolean }>(`/api/organizations/${orgId}`, { method: "DELETE" });
}

/* The tenant's one operating currency (CUR-205): an admin or a manager of the
 * organization reads it; only a platform admin assigns it, once. Both answers are
 * read for the requested organization only, so an answer naming another one is
 * `invalid`, never shown or confirmed for this one. */

export async function fetchOrganizationOperatingCurrency(orgId: number): Promise<ManagementRead<OperatingCurrencyAssignmentV2>> {
  return readOperatingCurrencyAssignmentV2(await http<unknown>(`/api/org/${orgId}/operating-currency`), orgId);
}

/** Assigns `operatingCurrency` when the stored value is still `expectedOperatingCurrency` (null: unassigned). */
export async function assignOrganizationOperatingCurrency(
  orgId: number,
  operatingCurrency: string,
  expectedOperatingCurrency: string | null,
): Promise<ManagementRead<OperatingCurrencyAssignmentV2>> {
  return readOperatingCurrencyAssignmentV2(await http<unknown>(`/api/org/${orgId}/operating-currency`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operating_currency: operatingCurrency, expected_operating_currency: expectedOperatingCurrency }),
  }), orgId);
}

/* The platform admin's switch of an organization's operating currency (CUR-207). Success
 * bodies are read strictly for the requested organization (and the activation for its own
 * request), so any other answer is `invalid`, never a basis or a confirmation. Refusals stay
 * the HttpError for classifySwitchRefusal; nothing is retried here. */

export async function fetchOperatingCurrencySwitchBasis(orgId: number): Promise<SwitchRead<OperatingCurrencySwitchBasis>> {
  return readSwitchBasis(await http<unknown>(`/api/org/${orgId}/operating-currency/switch`), orgId);
}

export async function activateOperatingCurrencySwitch(orgId: number, body: OperatingCurrencySwitchRequest): Promise<SwitchRead<OperatingCurrencySwitchCommitted>> {
  return readSwitchCommitted(await http<unknown>(`/api/org/${orgId}/operating-currency/switch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }), orgId, body);
}

/** Re-applies the post-commit effects of the committed `operatingCurrencyRevision`. */
export async function repairOperatingCurrencySwitch(orgId: number, operatingCurrencyRevision: number): Promise<SwitchRead<OperatingCurrencySwitchRepaired>> {
  return readSwitchRepaired(await http<unknown>(`/api/org/${orgId}/operating-currency/switch/effects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operating_currency_revision: operatingCurrencyRevision }),
  }), orgId, operatingCurrencyRevision);
}

export async function addUserToOrganization(
  orgId: number, userId: number, role: "manager" | "driver",
) {
  // Role is REQUIRED (no default) — the caller must choose, and the backend
  // rejects an omitted role. 'manager' = org-management; 'driver' = restricted
  // charging-only membership.
  return http<{ success: boolean }>(`/api/organizations/${orgId}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, role }),
  });
}

export async function removeUserFromOrganization(orgId: number, userId: number) {
  return http<{ success: boolean }>(`/api/organizations/${orgId}/users/${userId}`, { method: "DELETE" });
}

export interface OrgInvitation {
  id: number;
  organization_id: number;
  phone: string;
  role: "manager" | "driver";
  status: "pending" | "consumed" | "revoked";
  created_at: string;
  invited_by_user_id: number | null;
  /** Resolved server-side (best-effort); null when the inviter is unknown or
   *  their account is gone. */
  invited_by_username: string | null;
  consumed_at?: string | null;
  consumed_by_user_id?: number | null;
}

/** Why an invite SMS was not sent. Mirrors organizations/invite_sms.py — the
 *  backend is authoritative, these are only for phrasing the operator's message. */
export type InviteSmsReason =
  | "channel_disabled"
  | "registration_disabled"
  | "invalid_destination"
  | "country_blocked"
  | "suppression_unavailable"
  | "suppressed"
  | "not_configured"
  | "destination_limit"
  | "global_limit"
  | "send_failed"
  | "error";

/** Result of inviting by phone: an existing user is added immediately
 *  (status "added"), otherwise a pending invitation is recorded (status
 *  "invited") and — when the SMS channel is enabled — texted.
 *
 *  Invites are idempotent per (org, phone): re-inviting returns the EXISTING
 *  pending invitation with `already_pending` set, and re-sends the text. */
export interface InviteResult {
  success: boolean;
  status: "added" | "invited";
  added?: Array<{ id: number; username: string }>;
  skipped?: Array<{ id: number; username: string }>;
  invitation?: OrgInvitation;
  already_pending?: boolean;
  sms_sent?: boolean;
  sms_reason?: InviteSmsReason;
}

export interface OrgInvitationsResponse {
  invitations: OrgInvitation[];
  /** Whether an invite could be texted at all right now. When it can't, the
   *  invitation is still recorded — the operator just has to pass the
   *  registration link on by hand. */
  sms_channel: { available: boolean; reason: InviteSmsReason | null };
  registration_link: string;
}

/** Add a person to an organization by phone number. If a user with that phone
 *  already exists they are added; otherwise a pending invitation is created.
 *  Re-posting the same phone+role is the RESEND path (no duplicate row). */
export async function inviteUserByPhone(
  orgId: number, phone: string, role: "manager" | "driver",
) {
  return http<InviteResult>(`/api/organizations/${orgId}/invitations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone, role }),
  });
}

/** Pending invitations for an org (all statuses with `includeInactive`). */
export async function fetchOrgInvitations(orgId: number, includeInactive = false) {
  const qs = includeInactive ? "?all=1" : "";
  return http<OrgInvitationsResponse>(`/api/organizations/${orgId}/invitations${qs}`);
}

export async function revokeOrgInvitation(orgId: number, invitationId: number) {
  return http<{ success: boolean }>(
    `/api/organizations/${orgId}/invitations/${invitationId}`, { method: "DELETE" });
}

export async function addUnitToOrganization(orgId: number, unitId: string) {
  return http<{ success: boolean }>(`/api/organizations/${orgId}/units`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unit_id: unitId }),
  });
}

export async function removeUnitFromOrganization(orgId: number, unitId: string) {
  return http<{ success: boolean }>(`/api/organizations/${orgId}/units/${encodeURIComponent(unitId)}`, { method: "DELETE" });
}

/** Optional filters shared by the org charging-sessions report (JSON + CSV).
 *  All default to "all" when omitted. user_id/unit_id are the report's filter
 *  dropdowns; user_id is the auth user's numeric id passed as a string. */
export interface OrgSessionsReportFilters {
  start?: string;
  end?: string;
  user_id?: string;
  unit_id?: string;
  /** Only sessions whose payment needs attention (filtered on the server, so rows,
   *  totals and CSV come from one selection). */
  attention?: boolean;
}

/** The report query of the JSON and the CSV alike: the filters in their fixed order, then one trailing money_contract=2. */
function orgSessionsReportQuery(opts?: OrgSessionsReportFilters): string {
  const params = new URLSearchParams();
  if (opts?.start) params.set("start", opts.start);
  if (opts?.end) params.set("end", opts.end);
  if (opts?.user_id) params.set("user_id", opts.user_id);
  if (opts?.unit_id) params.set("unit_id", opts.unit_id);
  if (opts?.attention) params.set("attention", "1");
  const qs = params.toString();
  return `?${qs ? `${qs}&` : ""}${MONEY_CONTRACT_2}`;
}

/** The admin CSV report URL for an org's charging sessions (optional filters), with money
 *  contract 2: the server's single-currency CSV artifact (route family F8). `csvCurrency`
 *  picks one currency's artifact of a mixed selection; it exists only on the CSV, so it is
 *  no report filter and the JSON query cannot carry it. */
export function organizationSessionsReportUrl(
  orgId: number,
  opts?: OrgSessionsReportFilters,
  csvCurrency?: string,
): string {
  const url = `/api/organizations/${orgId}/sessions/report.csv${orgSessionsReportQuery(opts)}`;
  return csvCurrency ? `${url}&currency=${encodeURIComponent(csvCurrency)}` : url;
}

export type RevenueSource =
  | "frozen"
  | "legacy_fallback"
  // Migrated rows: commissions zeroed (nothing captured), not frozen.
  | "uncollected"
  | "free_charging_schedule";

/**
 * Factual payment-display states (see organizations/session_report.classify_payment).
 * These describe the persisted state; they do NOT judge recoverability.
 */
// Completed-sessions-only report: there is no "in progress" state.
export type PaymentState =
  | "paid"
  | "free"
  | "billing_processing"               // ended recently, finalizing normally
  | "billing_stuck"                    // no finalize progress within the alert window
  | "captured_amount_unverified"       // money captured but no finalized invoice to verify
  | "authorized_awaiting_capture"
  | "capture_failed"
  | "partially_captured"
  | "customer_action_required"
  | "payment_locked"
  | "canceled"
  | "abandoned"
  | "billing_not_finalized"
  | "billing_error"
  | "missing_payment_intent"
  | "data_mismatch"
  // Migrated (Energic) sessions billed under a real tariff but with nothing captured:
  // either an uncollected gap (with its root cause) or free-by-grant (Free Charging
  // Schedule). Factual/terminal — never actionable attention.
  | "uncollected_no_payment_method"
  | "uncollected_charger_offline"
  | "uncollected_payment_failed"
  | "uncollected_authorized_not_captured"
  | "free_charging_schedule"
  // Provisional session closed before any commitment (billing_status not_authorized):
  // no bill, no charge.
  | "not_authorized"
  | "unknown";

/** Attention category — how a flagged session should be triaged. */
export type AttentionCategory =
  | "payment_action_required"           // Stripe/payment op can be retried or inspected
  | "engineering_investigation_required" // missing PI / inconsistent data / not finalized
  | "manual_review_required";           // recoverability unknown from persisted facts

/**
 * Payment facts for one session — the backend response contract projected by
 * organizations/session_report.classify_payment and rendered by the shared
 * PaymentCell / PaymentBadge. Defined here (not in the presentation component) so
 * the API model owns the contract.
 *
 * `captured_payment` is the SINGLE canonical captured figure: the same
 * revenue-basis value as the org report's Captured column and CSV. There is no
 * divergent `captured_amount`.
 *
 * The top block is OWNER-visible (per-charger Session History). The fields below
 * the divider are ADMIN-ONLY Stripe / triage diagnostics — present only for
 * system admins (and in the admin org report), absent from a plain owner payload.
 */
export interface PaymentInfo {
  payment_status: string | null;
  billing_status?: string | null;
  payment_state: PaymentState;
  attention_reason: string | null;
  payment_needs_attention: boolean;
  billed_amount?: number | null;
  captured_payment: number | null;
  amount_due: number | null;
  authorized_hold_amount: number | null;
  outstanding_known: boolean;
  outstanding_amount: number | null;
  // ── Admin-only diagnostics (absent for non-admin callers) ──
  attention_category?: AttentionCategory | null;
  payment_intent_id?: string | null;
  /** A CURRENT billing failure only. */
  billing_error?: string | null;
  /** An earlier attempt's error on a row whose billing has since settled. */
  resolved_billing_error?: string | null;
  payment_last_decline_code?: string | null;
  payment_last_decline_at?: string | null;
  payment_hard_decline_count?: number;
}

/** How far a session's charging / idle / suspended split can be trusted (backend
 *  domain/session_durations.py):
 *  - measured: all three are the recorded totals;
 *  - charging_only: charging is exact, idle/suspended were never recorded;
 *  - partial: the stored intervals have a problem (see DurationIssue), sums are inexact;
 *  - unknown: nothing beyond plugged-in time is known. */
export type DurationBreakdown = "measured" | "partial" | "charging_only" | "unknown";

/** Why a split is `partial`. Direction differs per issue, so a partial value is never a
 *  bound: possibly_truncated / unknown_segment_type may under-count; overlap /
 *  unsealed_segment may over-count; out_of_bounds was clipped (direction unknown). */
export type DurationIssue =
  | "possibly_truncated"
  | "unknown_segment_type"
  | "unsealed_segment"
  | "overlap"
  | "out_of_bounds";

export type BilledAmountBasis = "invoice" | "estimate" | "unknown";

/** frozen_policy = the policy frozen with the session's invoice; legacy_formula = the
 *  historical report formula for sessions without one; not_charged = nothing captured
 *  (migrated uncollected / free schedule), commissions zeroed. */
export type ProcessingFeeBasis = "frozen_policy" | "legacy_formula" | "not_charged";

export interface OrgSessionsReportRange {
  start: string;           // YYYY-MM-DD (UTC)
  end: string;             // YYYY-MM-DD (UTC), inclusive
  defaulted: boolean;      // true when no dates were given (last 31 days)
  basis: "session_start_utc";
}

export interface OrgSessionsReportDriver {
  user_id: number;
  name: string | null;
  session_count: number;
}

/** Fetch the org charging-sessions report (rows + totals) for in-UI display, with money
 *  contract 2 (route family F8). */
export async function fetchOrganizationSessionsReport(
  orgId: number,
  opts?: OrgSessionsReportFilters,
): Promise<OrgSessionsReportRead> {
  return readOrgSessionsReport(
    await http<unknown>(`/api/organizations/${orgId}/sessions/report${orgSessionsReportQuery(opts)}`),
  );
}

/**
 * Download the org charging-sessions CSV report on money contract 2. Returns the server's
 * blob, byte for byte, and its filename. Throws HttpError (status and parsed body) on
 * failure, e.g. the 409 `mixed_currency_set` when the selection spans currencies.
 * Uses a raw request (not `http`) because the response is a CSV blob, not JSON.
 */
export async function downloadOrganizationSessionsReport(
  orgId: number,
  opts?: OrgSessionsReportFilters,
  csvCurrency?: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await request(organizationSessionsReportUrl(orgId, opts, csvCurrency), {
    credentials: "include",
  });
  if (!res.ok) throw await responseError(res);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m ? m[1] : `org-${orgId}-sessions.csv`;
  return { blob, filename };
}

/* ------------------------------------------------------------------ */
/*  Access Control (Driver Groups, Charger Groups, Policies)           */
/* ------------------------------------------------------------------ */

export interface DriverGroup {
  id: number;
  org_id: number;
  name: string;
  is_builtin: boolean;
  member_count: number;
}

export interface ChargerGroup {
  id: number;
  org_id: number;
  name: string;
  member_count: number;
}

export interface AccessPolicy {
  id: number;
  org_id: number;
  name: string;
  rule_type: "grant" | "deny";
  driver_group_id: number;
  charger_group_id: number;
  priority: number;
  enabled: boolean;
  rule_id?: number | null;
}

export interface AccessRule {
  id: number;
  org_id: number;
  name: string;
  rule_type: "allow_only" | "block" | "allow";
  driver_group_id: number;
  charger_group_id: number;
  enabled: boolean;
}

// Driver groups
export async function fetchDriverGroups(orgId: number): Promise<{ driver_groups: DriverGroup[] }> {
  return http<{ driver_groups: DriverGroup[] }>(`/api/org/${orgId}/driver-groups`);
}
export async function createDriverGroup(orgId: number, name: string) {
  return http<{ success: boolean; driver_group: DriverGroup }>(`/api/org/${orgId}/driver-groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}
export async function deleteDriverGroup(orgId: number, groupId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/driver-groups/${groupId}`, { method: "DELETE" });
}
export async function fetchDriverGroupMembers(orgId: number, groupId: number): Promise<{ members: number[] }> {
  return http<{ members: number[] }>(`/api/org/${orgId}/driver-groups/${groupId}/members`);
}
export async function addDriverGroupMember(orgId: number, groupId: number, userId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/driver-groups/${groupId}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
}
export async function removeDriverGroupMember(orgId: number, groupId: number, userId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/driver-groups/${groupId}/members/${userId}`, { method: "DELETE" });
}

// Charger groups
export async function fetchChargerGroups(orgId: number): Promise<{ charger_groups: ChargerGroup[] }> {
  return http<{ charger_groups: ChargerGroup[] }>(`/api/org/${orgId}/charger-groups`);
}
export async function createChargerGroup(orgId: number, name: string) {
  return http<{ success: boolean; charger_group: ChargerGroup }>(`/api/org/${orgId}/charger-groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}
export async function deleteChargerGroup(orgId: number, groupId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/charger-groups/${groupId}`, { method: "DELETE" });
}
export async function fetchChargerGroupMembers(orgId: number, groupId: number): Promise<{ members: string[] }> {
  return http<{ members: string[] }>(`/api/org/${orgId}/charger-groups/${groupId}/members`);
}
export async function addChargerGroupMember(orgId: number, groupId: number, unitId: string) {
  return http<{ success: boolean }>(`/api/org/${orgId}/charger-groups/${groupId}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unit_id: unitId }),
  });
}
export async function removeChargerGroupMember(orgId: number, groupId: number, unitId: string) {
  return http<{ success: boolean }>(`/api/org/${orgId}/charger-groups/${groupId}/members/${encodeURIComponent(unitId)}`, { method: "DELETE" });
}

// Access policies
export async function fetchAccessPolicies(orgId: number, includeManagedPolicies = false): Promise<{ access_policies: AccessPolicy[] }> {
  const qs = includeManagedPolicies ? '?include_managed=true' : '';
  return http<{ access_policies: AccessPolicy[] }>(`/api/org/${orgId}/access-policies${qs}`);
}
export async function createAccessPolicy(orgId: number, payload: {
  name: string; rule_type: string; driver_group_id: number; charger_group_id: number; priority?: number; enabled?: boolean;
}) {
  return http<{ success: boolean; access_policy: AccessPolicy }>(`/api/org/${orgId}/access-policies`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
export async function updateAccessPolicy(orgId: number, policyId: number, payload: Partial<{
  name: string; rule_type: string; priority: number; enabled: boolean;
  driver_group_id: number; charger_group_id: number;
}>) {
  return http<{ success: boolean }>(`/api/org/${orgId}/access-policies/${policyId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
export async function deleteAccessPolicy(orgId: number, policyId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/access-policies/${policyId}`, { method: "DELETE" });
}

// Access rules (high-level operator-facing intent)
export async function fetchAccessRules(orgId: number): Promise<{ access_rules: AccessRule[] }> {
  return http<{ access_rules: AccessRule[] }>(`/api/org/${orgId}/access-rules`);
}
export async function createAccessRule(orgId: number, payload: {
  name: string; rule_type: string; driver_group_id: number; charger_group_id: number;
}) {
  return http<{ success: boolean; access_rule: AccessRule }>(`/api/org/${orgId}/access-rules`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
export async function updateAccessRule(orgId: number, ruleId: number, payload: Partial<{
  name: string; enabled: boolean; driver_group_id: number; charger_group_id: number;
}>) {
  return http<{ success: boolean }>(`/api/org/${orgId}/access-rules/${ruleId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
export async function deleteAccessRule(orgId: number, ruleId: number) {
  return http<{ success: boolean }>(`/api/org/${orgId}/access-rules/${ruleId}`, { method: "DELETE" });
}
export async function fetchRulePolicies(orgId: number, ruleId: number): Promise<{ policies: AccessPolicy[] }> {
  return http<{ policies: AccessPolicy[] }>(`/api/org/${orgId}/access-rules/${ruleId}/policies`);
}

/* ------------------------------------------------------------------ */
/*  Load Balancing                                                     */
/* ------------------------------------------------------------------ */

// An org MANAGER scopes every load-balancing request to their selected org
// (/api/org/<id>/load-balancing/...); a system admin omits orgId and uses the fleet
// endpoint. There is no singular-org fallback — orgId is always passed explicitly.
function lbBase(orgId?: number | null): string {
  return orgId != null ? `/api/org/${orgId}/load-balancing` : "/api/load-balancing";
}

export async function fetchLoadBalancingGroups(orgId?: number | null): Promise<{ groups: LBGroup[] }> {
  const res = await http<{ groups: LBGroup[] }>(`${lbBase(orgId)}/groups`);
  // The read-model exposes per-unit minimum as `min_amps_per_unit`; surface it as
  // `min_amps` so the edit form prefills a real value (and never PUTs null, which the
  // backend now also guards against).
  const groups = (res.groups ?? []).map((g) => ({
    ...g,
    min_amps: (g.min_amps ?? (g as Record<string, unknown>).min_amps_per_unit ?? 6) as number,
  }));
  return { groups };
}

export async function createLoadBalancingGroup(
  group: { group_id: string; name: string; max_amps: number; min_amps?: number },
  orgId?: number | null,
) {
  return http<{ success: boolean; group: LBGroup }>(`${lbBase(orgId)}/groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(group),
  });
}

export async function updateLoadBalancingGroup(
  groupId: string, patch: { name?: string; max_amps?: number; min_amps?: number }, orgId?: number | null,
) {
  return http<{ success: boolean }>(`${lbBase(orgId)}/groups/${encodeURIComponent(groupId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteLoadBalancingGroup(groupId: string, orgId?: number | null) {
  return http<{ success: boolean }>(`${lbBase(orgId)}/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

export async function addUnitToGroup(groupId: string, unitId: string, orgId?: number | null) {
  return http<{ success: boolean }>(`${lbBase(orgId)}/groups/${encodeURIComponent(groupId)}/units`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unit_id: unitId }),
  });
}

export async function removeUnitFromGroup(groupId: string, unitId: string, orgId?: number | null) {
  return http<{ success: boolean }>(`${lbBase(orgId)}/groups/${encodeURIComponent(groupId)}/units/${encodeURIComponent(unitId)}`, { method: "DELETE" });
}

export async function fetchGroupStatus(groupId: string, orgId?: number | null): Promise<LBGroupStatus> {
  return http<LBGroupStatus>(`${lbBase(orgId)}/groups/${encodeURIComponent(groupId)}/status`);
}

/* ------------------------------------------------------------------ */
/*  EVSE Emulator                                                      */
/* ------------------------------------------------------------------ */

export interface EmulatorUnit {
  unit_id: string;
  running: boolean;
  state: number;
  state_name: string;
  voltage: number;
  current: number;
  temperature: number;
  session_energy: number;
  battery_soc: number;
  session_num: number;
  uptime: number;
  online_limit: number;
  offline_limit: number;
  unit_rating: number;
  interval: number;
  created_at: string;
  last_response: string | null;
  log: Array<{ ts: string; msg: string }>;
}

export async function fetchEmulatorUnits(): Promise<{ units: EmulatorUnit[] }> {
  return http<{ units: EmulatorUnit[] }>("/api/emulator/units");
}

export async function createEmulatorUnit(params: {
  unit_id: string;
  interval?: number;
  unit_rating?: number;
  auto_start?: boolean;
}): Promise<EmulatorUnit> {
  return http<EmulatorUnit>("/api/emulator/units", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
}

export async function deleteEmulatorUnit(unitId: string): Promise<{ ok: boolean }> {
  return http<{ ok: boolean }>(`/api/emulator/units/${encodeURIComponent(unitId)}`, { method: "DELETE" });
}

export async function emulatorAction(unitId: string, action: string, extra?: Record<string, unknown>): Promise<EmulatorUnit> {
  return http<EmulatorUnit>(`/api/emulator/units/${encodeURIComponent(unitId)}/action`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...extra }),
  });
}

/* ------------------------------------------------------------------ */
/*  Pando charger health                                               */
/* ------------------------------------------------------------------ */

export type PandoAction = "probe" | "rotate" | "cleanup";
export type PandoStatus = "ok" | "error" | "unreachable" | "timeout" | "auth_failed";

export interface PandoHealthRow {
  serial: string;
  ts: string;
  action: PandoAction;
  status: PandoStatus;
  tmp_used_mb: number | null;
  tmp_total_mb: number | null;
  logs_dir_mb: number | null;
  before_mb: number | null;
  after_mb: number | null;
  uptime_sec: number | null;
  error: string | null;
  last_ok_ts?: string | null;
}

export async function fetchPandoChargers(): Promise<{ chargers: PandoHealthRow[] }> {
  return http<{ chargers: PandoHealthRow[] }>("/api/pando/chargers");
}

export async function fetchPandoChargerHistory(
  serial: string,
  limit = 50,
): Promise<{ serial: string; history: PandoHealthRow[] }> {
  const url = new URL(`/api/pando/chargers/${encodeURIComponent(serial)}/history`, window.location.origin);
  url.searchParams.set("limit", String(limit));
  return http<{ serial: string; history: PandoHealthRow[] }>(url.toString());
}

/* ------------------------------------------------------------------ */
/*  Special rates — pricing profiles (Stage 1A) + tariff assignments  */
/*  (Stage 1B). Org-scoped, platform-admin-only on the backend.       */
/*  The frontend NEVER resolves a tariff for a session — it only reads */
/*  and writes catalog rows; the backend owns immutable resolution.   */
/* ------------------------------------------------------------------ */

export interface PricingProfile {
  id: number;
  organization_id: number;
  name: string;
  pricebook: Pricebook;
  pricebook_version: string;
  revision: number;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export type AudienceType = "driver_group" | "user" | "authenticated";
export type ResourceType = "unit" | "charger_group";

export interface TariffAssignment {
  id: number;
  organization_id: number;
  pricing_profile_id: number;
  audience_type: AudienceType;
  user_id: number | null;
  driver_group_id: number | null;
  resource_type: ResourceType;
  unit_id: string | null;
  charger_group_id: number | null;
  valid_from_utc: string | null;
  valid_until_utc: string | null;
  enabled: boolean;
  revision: number;
  created_at: string | null;
  updated_at: string | null;
  pricing_profile?: {
    id: number;
    name: string;
    revision: number;
    pricebook_version: string;
    enabled: boolean;
  };
}

/**
 * WRITE shape of a pricebook. Financial values are sent as canonical decimal
 * STRINGS (e.g. "0.15"), never JS numbers: the backend parses a legacy
 * rate_base string as an exact Decimal, so the rate is exact and float-free.
 * Reads take rates from the money contract 2 projection, never from a legacy
 * numeric `Pricebook`.
 */
export interface RateRuleWrite {
  fee_type: FeeType;
  uom: Uom;
  rate_base: string;
  is_tou?: boolean;
  start_time?: string;
  end_time?: string;
  days?: string;
  priority?: number;
}
export interface PricebookWrite {
  currency_code: string;
  effective_pricebook: RateRuleWrite[];
}

export interface PricingProfilePayload {
  name: string;
  // Canonical pricebook with exact decimal-string rates - the SAME shape the charger
  // default tariff saves, produced by lib/pricingForm.valuesToPricebook.
  pricebook: PricebookWrite;
  expected_revision?: number;
}

export interface TariffAssignmentPayload {
  pricing_profile_id: number;
  audience_type: AudienceType;
  user_id: number | null;
  driver_group_id: number | null;
  resource_type: ResourceType;
  unit_id: string | null;
  charger_group_id: number | null;
  valid_from_utc: string | null;
  valid_until_utc: string | null;
  expected_revision?: number;
}

export async function fetchPricingProfiles(orgId: number): Promise<ManagementRead<PricingProfileListManagementV2>> {
  return readPricingProfileListResponseV2(await http<unknown>(`/api/org/${orgId}/pricing-profiles?${MONEY_CONTRACT_2}`));
}

export async function createPricingProfile(orgId: number, payload: PricingProfilePayload): Promise<ManagementRead<PricingProfileManagementV2>> {
  return readPricingProfileResponseV2(await http<unknown>(`/api/org/${orgId}/pricing-profiles?${MONEY_CONTRACT_2}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: payload.name, pricebook: payload.pricebook }),
  }));
}

export async function updatePricingProfile(orgId: number, profileId: number, payload: PricingProfilePayload): Promise<ManagementRead<PricingProfileManagementV2>> {
  return readPricingProfileResponseV2(await http<unknown>(`/api/org/${orgId}/pricing-profiles/${profileId}?${MONEY_CONTRACT_2}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }));
}

/** Soft-delete: the backend has no hard DELETE for profiles — disable is the
 *  canonical retire path (idempotent, revisioned). */
export async function disablePricingProfile(orgId: number, profileId: number, expectedRevision?: number): Promise<ManagementRead<PricingProfileManagementV2>> {
  return readPricingProfileResponseV2(await http<unknown>(`/api/org/${orgId}/pricing-profiles/${profileId}/disable?${MONEY_CONTRACT_2}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(expectedRevision != null ? { expected_revision: expectedRevision } : {}),
  }));
}

export async function fetchTariffAssignments(orgId: number): Promise<{ tariff_assignments: TariffAssignment[] }> {
  return http<{ tariff_assignments: TariffAssignment[] }>(`/api/org/${orgId}/tariff-assignments`);
}

export async function createTariffAssignment(orgId: number, payload: TariffAssignmentPayload) {
  const { expected_revision, ...body } = payload;
  return http<{ tariff_assignment: TariffAssignment }>(`/api/org/${orgId}/tariff-assignments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateTariffAssignment(orgId: number, assignmentId: number, payload: TariffAssignmentPayload) {
  return http<{ tariff_assignment: TariffAssignment }>(`/api/org/${orgId}/tariff-assignments/${assignmentId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Soft-delete: disable is the canonical retire path for assignments. */
export async function disableTariffAssignment(orgId: number, assignmentId: number, expectedRevision?: number) {
  return http<{ tariff_assignment: TariffAssignment }>(`/api/org/${orgId}/tariff-assignments/${assignmentId}/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(expectedRevision != null ? { expected_revision: expectedRevision } : {}),
  });
}

// ── Pricing domain (driver enrollment + pricing driver groups + org-wide
//    assignments; pricing varies ONLY by driver identity — no charger dimension) ──

export type PricingAudienceType = "user" | "pricing_driver_group";

export interface PricingGroup {
  id: number;
  organization_id: number;
  name: string;
  is_builtin: boolean;
  member_count?: number;
}

export interface PricingAssignment {
  id: number;
  organization_id: number;
  pricing_profile_id: number;
  audience_type: PricingAudienceType;
  audience_id: number;
  enabled: boolean;
  revision: number;
  profile_name?: string;
}

export async function fetchPricingDrivers(orgId: number): Promise<{ drivers: number[] }> {
  return http<{ drivers: number[] }>(`/api/org/${orgId}/pricing/drivers`);
}
export async function enrollPricingDriver(orgId: number, userId: number) {
  return http(`/api/org/${orgId}/pricing/drivers`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
}
export async function unenrollPricingDriver(orgId: number, userId: number) {
  return http(`/api/org/${orgId}/pricing/drivers/${userId}`, { method: "DELETE" });
}

export async function fetchPricingGroups(orgId: number): Promise<{ pricing_groups: PricingGroup[] }> {
  return http<{ pricing_groups: PricingGroup[] }>(`/api/org/${orgId}/pricing/groups`);
}
export async function createPricingGroup(orgId: number, name: string) {
  return http<{ pricing_group: PricingGroup }>(`/api/org/${orgId}/pricing/groups`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}
export async function renamePricingGroup(orgId: number, groupId: number, name: string) {
  return http(`/api/org/${orgId}/pricing/groups/${groupId}`, {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}
export async function deletePricingGroup(orgId: number, groupId: number) {
  return http(`/api/org/${orgId}/pricing/groups/${groupId}`, { method: "DELETE" });
}
export async function fetchPricingGroupMembers(orgId: number, groupId: number): Promise<{ members: number[] }> {
  return http<{ members: number[] }>(`/api/org/${orgId}/pricing/groups/${groupId}/members`);
}
export async function addPricingGroupMember(orgId: number, groupId: number, userId: number) {
  return http(`/api/org/${orgId}/pricing/groups/${groupId}/members`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
}
export async function removePricingGroupMember(orgId: number, groupId: number, userId: number) {
  return http(`/api/org/${orgId}/pricing/groups/${groupId}/members/${userId}`, { method: "DELETE" });
}

export async function fetchPricingAssignments(orgId: number): Promise<{ assignments: PricingAssignment[] }> {
  return http<{ assignments: PricingAssignment[] }>(`/api/org/${orgId}/pricing/assignments`);
}
export async function createPricingAssignment(
  orgId: number, body: { pricing_profile_id: number; audience_type: PricingAudienceType; audience_id: number },
) {
  return http<{ assignment: PricingAssignment }>(`/api/org/${orgId}/pricing/assignments`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}
export async function disablePricingAssignment(orgId: number, assignmentId: number, expectedRevision?: number) {
  return http(`/api/org/${orgId}/pricing/assignments/${assignmentId}/disable`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(expectedRevision != null ? { expected_revision: expectedRevision } : {}),
  });
}

/* ------------------------------------------------------------------ */
/*  System health                                                      */
/* ------------------------------------------------------------------ */

/**
 * Admin-only per-component health breakdown. Not under /api — Flask serves the
 * health family at /health/* (see web_server.py) — but it is the same session
 * cookie / JWT, so it goes through the shared http client like everything else.
 */
export async function fetchHealthStatus(): Promise<HealthStatus> {
  return http<HealthStatus>("/health/status");
}
